import React, { useState, useCallback } from 'react';
import PlannerCalendarView from './viewComponents/PlannerCalendarView';
import ModalEventForm from '../tab_events/viewComponents/ModalEventForm';
import { deleteEvent } from '../moduleServices/planner_events_api';
import { toast } from 'react-hot-toast';
import { defaultCalendarIdFromMultiFilterStorage } from '../moduleServices/planner_calendar_multi_filter';
import { PLANNER_TIME_FORMAT_24 } from '../moduleServices/planner_time_format';

const PLANNER_CALENDAR_TAB_FILTER_KEY = 'planner_calendar_calendar_filter';

export default function MainCalendar({
  events,
  eventsWindow = null,
  isEventsLoading,
  refreshEvents,
  updateEventsOptimistically,
  calendars,
  weekRowsVisible = 6,
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const selectedEvent = React.useMemo(() => {
    if (!selectedEventId) return null;
    return events.find((e) => e.id === selectedEventId) || null;
  }, [events, selectedEventId]);

  const handleEventClick = (event) => {
    setSelectedEventId(event.id);
    setSelectedDate(null);
    setIsModalOpen(true);
  };

  const handleNewEventForDate = useCallback((dateKey) => {
    setSelectedDate(dateKey);
    setSelectedEventId(null);
    setIsModalOpen(true);
  }, []);

  const handleDeleteEvent = async (eventId) => {
    try {
      await deleteEvent(eventId);
      await refreshEvents();
      if (selectedEventId === eventId) {
        setIsModalOpen(false);
        setSelectedEventId(null);
      }
      toast.success('Event deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete event');
      throw error;
    }
  };

  const handleOptimisticUpdate = (updatedEventData) => {
    if (updatedEventData && updatedEventData.id) {
      updateEventsOptimistically(updatedEventData);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEventId(null);
    setSelectedDate(null);
  };

  const getDefaultCalendarId = () => {
    try {
      return defaultCalendarIdFromMultiFilterStorage(
        localStorage.getItem(PLANNER_CALENDAR_TAB_FILTER_KEY)
      );
    } catch {
      return null;
    }
  };

  const getInitialFormData = () => {
    if (!selectedDate) return null;
    return {
      is_all_day: true,
      date_range: { start: selectedDate, end: selectedDate },
      time_range: { start: '12:00', end: '12:00' },
      calendar_id: getDefaultCalendarId(),
    };
  };

  return (
    <>
      <div className="flex flex-col" style={{ height: 'calc(100vh - 3.5rem - 5rem)' }}>
        <PlannerCalendarView
          events={events}
          eventsWindow={eventsWindow}
          isEventsLoading={isEventsLoading}
          calendars={calendars}
          weekRowsVisible={weekRowsVisible}
          refreshEvents={refreshEvents}
          onEventClick={handleEventClick}
          onNewEventForDate={handleNewEventForDate}
          timeFormatPreference={timeFormatPreference}
        />
      </div>

      <ModalEventForm
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onOptimisticUpdate={handleOptimisticUpdate}
        editEvent={selectedEvent}
        onDelete={handleDeleteEvent}
        calendars={calendars}
        defaultCalendarId={getDefaultCalendarId()}
        initialFormData={getInitialFormData()}
        timeFormatPreference={timeFormatPreference}
      />
    </>
  );
}
